import { ApplicationRef, inject, Injectable } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { concat, filter, first, interval } from 'rxjs';

import { stashAuthReturnUrl } from '@shared/utils';

/** 7:30 PM Central, in minutes from midnight. */
const END_OF_WORKDAY = 1170;

/** 5:00 AM Central, in minutes from midnight. */
const BEGINNING_OF_WORKDAY = 300;

/**
 * Checks for a new app version after hours and reloads onto that version
 * without sending the user through the default home/main-menu route.
 */
@Injectable({ providedIn: 'root' })
export class AppUpdateService {
  private readonly updates = inject(SwUpdate);
  private readonly appRef = inject(ApplicationRef);

  /**
   * Starts after-hours update checks and applies a ready service-worker
   * version with a full reload.
   */
  start(): void {
    if (!this.updates.isEnabled) {
      return;
    }

    const appIsStable$ = this.appRef.isStable.pipe(first((isStable) => isStable === true));
    const everyMinute$ = interval(60 * 1000);
    const everyMinuteOnceAppIsStable$ = concat(appIsStable$, everyMinute$);

    everyMinuteOnceAppIsStable$.subscribe(() => {
      try {
        if (this.isAfterHoursCentral()) {
          void this.updates.checkForUpdate();
        }
      } catch (err) {
        console.error('Failed to check for updates:', err);
      }
    });

    this.updates.versionUpdates.pipe(filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY')).subscribe(() => {
      stashAuthReturnUrl(`${location.pathname}${location.search}${location.hash}`);
      console.log('A new version is ready. Reloading the application...');
      void this.updates.activateUpdate().then(() => {
        location.reload();
      });
    });
  }

  /**
   * True from 7:30 PM through 5:00 AM America/Chicago.
   */
  private isAfterHoursCentral(): boolean {
    const centralTime = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Chicago' }));
    const now = centralTime.getHours() * 60 + centralTime.getMinutes();
    return now >= END_OF_WORKDAY || now < BEGINNING_OF_WORKDAY;
  }
}
